import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowRight, Loader2, Crosshair, Shield, Skull, Eye, Sparkles } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, user } = useAuth();
  const { mode, theme } = useTheme();
  const isExecDark = mode === 'executive' && theme === 'dark';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back to Sub-Zero!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className={`min-h-screen relative overflow-hidden flex ${mode === 'executive' ? 'bg-gradient-to-br from-sky-50 via-white to-blue-50' : 'bg-background'}`}>
      {/* Background effects */}
      {mode !== 'executive' && <div className="fixed inset-0 cyber-grid opacity-30 pointer-events-none" />}

      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-12 py-12 relative z-20">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-3 mb-10">
            {mode === 'executive' ? (
              <>
                <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span className="font-display font-bold text-lg text-foreground">Sub-Zero</span>
              </>
            ) : (
              <>
                <div className="w-10 h-10 bg-primary rounded flex items-center justify-center glow-primary">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <div>
                  <span className="font-display font-bold text-lg text-foreground">SUB-ZERO</span>
                  <span className="block text-[10px] font-mono text-primary tracking-widest">GHOST AUDITOR v2.0</span>
                </div>
              </>
            )}
          </Link>

          {mode === 'executive' ? (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5 text-sky-500" />
              <span className="text-sky-600 text-xs font-medium">Welcome back</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded mb-6">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-primary text-xs font-mono tracking-wider">SECURE LOGIN</span>
            </div>
          )}

          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-2 leading-tight">
            {mode === 'executive' ? (
              <>Welcome Back</>
            ) : (
              <>Welcome Back, <span className="text-primary">Agent</span>.</>
            )}
          </h1>
          <p className="text-muted-foreground mb-8">
            {mode === 'executive' ? 'Sign in to manage your subscriptions.' : 'Sign in to access your subscription command center.'}
          </p>

          <Button
            type="button"
            variant="outline"
            className={`w-full mb-6 text-foreground h-12 font-medium transition-all ${mode === 'executive' ? 'border-gray-200 hover:bg-gray-50 rounded-xl' : 'border-border hover:bg-white/5 hover:border-primary/50 rounded'}`}
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className={`bg-background px-3 text-muted-foreground ${mode === 'executive' ? 'bg-gradient-to-r from-sky-50 via-white to-blue-50' : 'font-mono'}`}>Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className={`text-foreground text-sm ${mode === 'executive' ? 'font-medium' : 'font-mono uppercase tracking-wider'}`}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email" type="email" placeholder={mode === 'executive' ? 'you@example.com' : 'agent@domain.com'}
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground/50 h-12 focus:border-primary focus:ring-primary/20 ${mode === 'executive' ? 'rounded-xl' : 'rounded'}`}
                  required data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={`text-foreground text-sm ${mode === 'executive' ? 'font-medium' : 'font-mono uppercase tracking-wider'}`}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password" type="password" placeholder={mode === 'executive' ? 'Enter your password' : 'Enter secure passphrase'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground/50 h-12 focus:border-primary focus:ring-primary/20 ${mode === 'executive' ? 'rounded-xl' : 'rounded'}`}
                  required data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className={`w-full h-12 font-bold ${mode === 'executive' ? 'bg-sky-500 text-white hover:bg-sky-600 rounded-xl shadow-lg shadow-sky-500/20' : 'bg-primary text-primary-foreground hover:bg-primary/90 rounded uppercase tracking-wide glow-primary'}`}
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === 'executive' ? 'Sign In' : 'Sign In'}<ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className={`hover:underline font-medium ${mode === 'executive' ? 'text-sky-500' : 'text-primary'}`} data-testid="register-link">
              {mode === 'executive' ? 'Create an account' : 'Deploy a new agent'}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className={`hidden lg:flex flex-1 items-center justify-center p-12 relative border-l ${mode === 'executive' ? (isExecDark ? 'border-border bg-card' : 'border-gray-100 bg-white') : 'border-border'}`}>
        {mode !== 'executive' && <div className="absolute inset-0 bg-card/50" />}
        {mode !== 'executive' && <div className="absolute inset-0 scanline-bg" />}
        <div className="relative z-10 max-w-md text-center space-y-8">
          {mode === 'executive' ? (
            <>
              <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto">
                <Zap className="w-10 h-10 text-sky-500" />
              </div>
              <h2 className="font-display text-2xl font-bold text-foreground">
                Smart subscription <span className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">management</span>
              </h2>
              <p className="text-muted-foreground">
                Track, cancel, and optimize all your recurring payments in one beautiful dashboard.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { icon: <Shield className="w-5 h-5 text-sky-500" />, label: "Bank-grade Security", desc: "Your data is encrypted" },
                  { icon: <Sparkles className="w-5 h-5 text-blue-500" />, label: "AI-Powered", desc: "Smart savings insights" },
                  { icon: <Eye className="w-5 h-5 text-emerald-500" />, label: "Auto-Detect", desc: "Find hidden subscriptions" },
                  { icon: <ArrowRight className="w-5 h-5 text-violet-500" />, label: "Easy Cancellation", desc: "One-click cancel" }
                ].map((item, idx) => (
                  <div key={idx} className={`${isExecDark ? 'bg-secondary border border-border' : 'bg-gray-50 border border-gray-100'} rounded-2xl p-4 text-left`}>
                    <div className="flex items-center gap-2 mb-1">{item.icon}<span className="text-xs text-foreground font-semibold">{item.label}</span></div>
                    <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-primary/10 border border-primary/30 rounded flex items-center justify-center mx-auto glow-primary">
                <Zap className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-display text-2xl font-bold text-foreground">
                Your subscriptions are <span className="text-destructive">bleeding money</span>.
              </h2>
              <p className="text-muted-foreground">
                The average user has 12 subscriptions and forgets about 3 of them. Sub-Zero finds them and <span className="text-destructive font-semibold">kills them</span>.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { icon: <Crosshair className="w-5 h-5 text-destructive" />, label: "AI Kill Switch", desc: "One-tap cancellation" },
                  { icon: <Skull className="w-5 h-5 text-yellow-500" />, label: "Zombie Killer", desc: "Auto-kill free trials" },
                  { icon: <Eye className="w-5 h-5 text-primary" />, label: "Auto-Detect", desc: "Find hidden trials" },
                  { icon: <Shield className="w-5 h-5 text-primary" />, label: "256-bit Security", desc: "Bank-level encryption" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-card/80 border border-border rounded p-3 text-left">
                    <div className="flex items-center gap-2 mb-1">{item.icon}<span className="text-xs font-mono text-foreground font-bold">{item.label}</span></div>
                    <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
